import React, { useState, useRef, useEffect } from 'react';
import { Upload, MousePointer, Type, Undo, Trash2, Download, RefreshCw, Sparkles, Send, X, Check, Zap, Shield, Clock } from 'lucide-react';

// Custom Ink Pen Icon
const InkPenIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19l7-7 3 3-7 7-3-3z"/>
    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
    <path d="M2 2l7.586 7.586"/>
    <circle cx="11" cy="11" r="2"/>
  </svg>
);

const PDFGenie = () => {
  const [showLanding, setShowLanding] = useState(true);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [elements, setElements] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedTool, setSelectedTool] = useState('select');
  const [selectedElement, setSelectedElement] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signaturePosition, setSignaturePosition] = useState(null);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [showAIModal, setShowAIModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [textInput, setTextInput] = useState(null);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [savedFields, setSavedFields] = useState({});
  const [placementMode, setPlacementMode] = useState(false);
  const [placementFields, setPlacementFields] = useState([]);
  const [placementIndex, setPlacementIndex] = useState(0);
  const [pdfLibLoaded, setPdfLibLoaded] = useState(false);
  
  const canvasRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [signaturePath, setSignaturePath] = useState([]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        setPdfLibLoaded(true);
      }
    };
    document.body.appendChild(script);
  }, []);

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }

    if (!window.pdfjsLib) {
      alert('PDF library is still loading. Please wait a moment and try again.');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const typedArray = new Uint8Array(e.target.result);
          const loadingTask = window.pdfjsLib.getDocument({ data: typedArray });
          const pdf = await loadingTask.promise;
          setPdfDoc(pdf);
          setTotalPages(pdf.numPages);
          setCurrentPage(1);
          await renderPage(pdf, 1);
        } catch (error) {
          console.error('Error loading PDF:', error);
          alert('Failed to load PDF. Please try another file.');
        }
      };
      reader.onerror = () => {
        alert('Failed to read file');
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error handling file upload:', error);
      alert('Error uploading file');
    }
  };

  const renderPage = async (pdf, pageNum) => {
    if (!pdf || !canvasRef.current) return;
    
    try {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
      drawElements();
    } catch (error) {
      console.error('Error rendering page:', error);
      alert('Failed to render PDF page');
    }
  };

  const drawElements = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    elements.forEach(el => {
      if (el.page !== currentPage) return;
      
      if (el.type === 'text') {
        const fontSize = el.fontSize || 20;
        ctx.font = `${fontSize}px Arial`;
        ctx.fillStyle = 'black';
        ctx.fillText(el.content, el.x, el.y);
      } else if (el.type === 'signature') {
        const img = new Image();
        img.src = el.data;
        img.onload = () => {
          ctx.drawImage(img, el.x, el.y, el.width, el.height);
        };
      }
      
      if (selectedElement === el.id) {
        ctx.strokeStyle = '#3B82F6';
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 2;
        const padding = 5;
        
        let bounds = {};
        if (el.type === 'text') {
          const metrics = ctx.measureText(el.content);
          bounds = {
            x: el.x - padding,
            y: el.y - (el.fontSize || 20) - padding,
            width: metrics.width + padding * 2,
            height: (el.fontSize || 20) + 10 + padding * 2
          };
          ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
          
          ctx.fillStyle = '#3B82F6';
          ctx.fillRect(
            bounds.x + bounds.width - 8,
            bounds.y + bounds.height - 8,
            8,
            8
          );
        } else if (el.type === 'signature') {
          bounds = {
            x: el.x - padding,
            y: el.y - padding,
            width: el.width + padding * 2,
            height: el.height + padding * 2
          };
          ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
          
          ctx.fillStyle = '#3B82F6';
          ctx.fillRect(
            bounds.x + bounds.width - 8,
            bounds.y + bounds.height - 8,
            8,
            8
          );
        }
        ctx.setLineDash([]);
      }
    });
  };

  useEffect(() => {
    if (pdfDoc && canvasRef.current) {
      renderPage(pdfDoc, currentPage);
    }
  }, [elements, selectedElement, currentPage, pdfDoc]);

  const handleCanvasClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (placementMode) {
      const field = placementFields[placementIndex];
      const newElement = {
        id: Date.now(),
        type: 'text',
        content: field.value,
        x,
        y,
        fontSize: 20,
        page: currentPage
      };
      
      saveHistory();
      setElements([...elements, newElement]);
      
      if (placementIndex < placementFields.length - 1) {
        setPlacementIndex(placementIndex + 1);
      } else {
        setPlacementMode(false);
        setPlacementFields([]);
        setPlacementIndex(0);
        alert('All fields placed successfully!');
      }
      return;
    }

    if (selectedTool === 'signature') {
      setSignaturePosition({ x, y, page: currentPage });
      setShowSignatureModal(true);
      return;
    }

    if (selectedTool === 'select') {
      let found = null;
      elements.forEach(el => {
        if (el.page !== currentPage) return;
        
        const ctx = canvasRef.current.getContext('2d');
        if (el.type === 'text') {
          const fontSize = el.fontSize || 20;
          ctx.font = `${fontSize}px Arial`;
          const metrics = ctx.measureText(el.content);
          if (x >= el.x && x <= el.x + metrics.width && y >= el.y - fontSize && y <= el.y) {
            found = el.id;
          }
        } else if (el.type === 'signature') {
          if (x >= el.x && x <= el.x + el.width && y >= el.y && y <= el.y + el.height) {
            found = el.id;
          }
        }
      });
      setSelectedElement(found);
    } else if (selectedTool === 'text') {
      setTextInput({ x, y, value: '' });
    }
  };

  const handleCanvasMouseDown = (e) => {
    if (selectedTool !== 'select' || !selectedElement) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const el = elements.find(e => e.id === selectedElement);
    if (!el) return;
    
    const ctx = canvasRef.current.getContext('2d');
    const padding = 5;
    
    let bounds = {};
    if (el.type === 'text') {
      const fontSize = el.fontSize || 20;
      ctx.font = `${fontSize}px Arial`;
      const metrics = ctx.measureText(el.content);
      bounds = {
        x: el.x - padding,
        y: el.y - fontSize - padding,
        width: metrics.width + padding * 2,
        height: fontSize + 10 + padding * 2
      };
    } else if (el.type === 'signature') {
      bounds = {
        x: el.x - padding,
        y: el.y - padding,
        width: el.width + padding * 2,
        height: el.height + padding * 2
      };
    }
    
    const handleSize = 8;
    const handleX = bounds.x + bounds.width - handleSize;
    const handleY = bounds.y + bounds.height - handleSize;
    
    if (x >= handleX && x <= handleX + handleSize && 
        y >= handleY && y <= handleY + handleSize) {
      setResizing({
        id: el.id,
        startX: x,
        startY: y,
        originalWidth: el.type === 'signature' ? el.width : bounds.width - padding * 2,
        originalHeight: el.type === 'signature' ? el.height : bounds.height - padding * 2,
        originalFontSize: el.fontSize || 20
      });
    } else {
      setDragging({ id: el.id, offsetX: x - el.x, offsetY: y - el.y });
    }
  };

  const handleCanvasMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (resizing) {
      const deltaX = x - resizing.startX;
      const deltaY = y - resizing.startY;
      
      // Use requestAnimationFrame to throttle updates
      requestAnimationFrame(() => {
        setElements(elements.map(el => {
          if (el.id === resizing.id) {
            if (el.type === 'signature') {
              const newWidth = Math.max(50, resizing.originalWidth + deltaX);
              const newHeight = Math.max(25, resizing.originalHeight + deltaY);
              return { ...el, width: newWidth, height: newHeight };
            } else if (el.type === 'text') {
              const avgDelta = (deltaX + deltaY) / 2;
              const scaleFactor = 1 + (avgDelta / 100);
              const newFontSize = Math.max(10, Math.min(100, resizing.originalFontSize * scaleFactor));
              return { ...el, fontSize: newFontSize };
            }
          }
          return el;
        }));
      });
    } else if (dragging) {
      // Use requestAnimationFrame to throttle updates
      requestAnimationFrame(() => {
        setElements(elements.map(el => 
          el.id === dragging.id 
            ? { ...el, x: x - dragging.offsetX, y: y - dragging.offsetY }
            : el
        ));
      });
    }
  };

  const handleCanvasMouseUp = () => {
    if (dragging || resizing) {
      saveHistory();
    }
    setDragging(null);
    setResizing(null);
  };

  const handleTextInput = (e) => {
    if (e.key === 'Enter') {
      if (textInput.value.trim()) {
        const newElement = {
          id: Date.now(),
          type: 'text',
          content: textInput.value,
          x: textInput.x,
          y: textInput.y,
          fontSize: 20,
          page: currentPage
        };
        saveHistory();
        setElements([...elements, newElement]);
      }
      setTextInput(null);
    } else if (e.key === 'Escape') {
      setTextInput(null);
    }
  };

  const saveHistory = () => {
    setHistory([...history, [...elements]]);
  };

  const handleUndo = () => {
    if (history.length > 0) {
      const previous = history[history.length - 1];
      setElements(previous);
      setHistory(history.slice(0, -1));
      setSelectedElement(null);
    }
  };

  const handleDelete = () => {
    if (selectedElement) {
      saveHistory();
      setElements(elements.filter(el => el.id !== selectedElement));
      setSelectedElement(null);
    }
  };

  const handleSignatureMouseDown = (e) => {
    setIsDrawing(true);
    const rect = signatureCanvasRef.current.getBoundingClientRect();
    setSignaturePath([{ x: e.clientX - rect.left, y: e.clientY - rect.top }]);
  };

  const handleSignatureMouseMove = (e) => {
    if (!isDrawing) return;
    
    const rect = signatureCanvasRef.current.getBoundingClientRect();
    const newPoint = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setSignaturePath([...signaturePath, newPoint]);
    
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    
    if (signaturePath.length > 0) {
      const prev = signaturePath[signaturePath.length - 1];
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(newPoint.x, newPoint.y);
      ctx.stroke();
    }
  };

  const handleSignatureMouseUp = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignaturePath([]);
  };

  const saveSignature = () => {
    if (signatureMode === 'draw') {
      if (signaturePath.length === 0) {
        alert('Please draw a signature first');
        return;
      }
      
      const canvas = signatureCanvasRef.current;
      const dataUrl = canvas.toDataURL();
      const newElement = {
        id: Date.now(),
        type: 'signature',
        data: dataUrl,
        x: signaturePosition.x,
        y: signaturePosition.y,
        width: 150,
        height: 75,
        page: signaturePosition.page
      };
      saveHistory();
      setElements([...elements, newElement]);
      
      // Clear signature before closing modal
      clearSignature();
    } else {
      // Type mode
      if (!typedSignature.trim()) {
        alert('Please type your signature first');
        return;
      }
      
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 450;
      tempCanvas.height = 200;
      const ctx = tempCanvas.getContext('2d');
      
      ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      ctx.font = 'italic 48px "Brush Script MT", cursive, serif';
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedSignature, tempCanvas.width / 2, tempCanvas.height / 2);
      
      const dataUrl = tempCanvas.toDataURL('image/png');
      const newElement = {
        id: Date.now(),
        type: 'signature',
        data: dataUrl,
        x: signaturePosition.x,
        y: signaturePosition.y,
        width: 150,
        height: 75,
        page: signaturePosition.page
      };
      saveHistory();
      setElements([...elements, newElement]);
    }
    
    // Close modal and reset state
    setShowSignatureModal(false);
    setSignaturePosition(null);
    setTypedSignature('');
    setSignaturePath([]);
    setSelectedTool('select');
  };

  const extractFieldsWithClaude = async (userMessage) => {
    const ANTHROPIC_API_KEY = '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';
    
    try {
      console.log('Calling Claude Haiku API...');
      
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-haiku-20240307',
          max_tokens: 500,
          messages: [{
            role: 'user',
            content: `You are a form field extraction assistant. Extract all form fields from the user's message and return ONLY a valid JSON object with field names as keys and their values.

Rules:
- Convert field names to lowercase
- Handle common abbreviations (dob = date of birth, ph/tel = phone, etc.)
- Extract dates in any format
- Return empty object {} if no fields found
- Do not include any markdown, explanations, or code blocks - just the raw JSON

User message: "${userMessage}"

Example outputs:
{"name": "John Doe", "email": "john@email.com"}
{"date of birth": "03/15/1990", "company": "TechCorp"}
{}`
          }]
        })
      });

      console.log('API Response Status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('Claude Response:', data);
      
      const content = data.content[0].text.trim();
      console.log('Extracted content:', content);
      
      // Clean up any markdown code blocks if present
      const jsonText = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      
      const parsed = JSON.parse(jsonText);
      console.log('Parsed fields:', parsed);
      
      return parsed;
    } catch (error) {
      console.error('Error calling Claude API:', error);
      console.log('Falling back to regex extraction');
      // Fallback to regex extraction
      return extractFieldsWithRegex(userMessage);
    }
  };

  const extractFieldsWithRegex = (message) => {
    // Fallback regex-based extraction (original method)
    const fields = {};
    const lowerMessage = message.toLowerCase();
    
    const patterns = [
      /([A-Za-z\s']+?):\s*(.+?)(?=\s*[,;]|\s*[A-Za-z\s']+?:|$)/gi,
      /(?:my|the)?\s*([A-Za-z\s']+?)\s+(?:is|are)\s+(.+?)(?=\s*[,;]|\s+(?:my|the)?\s*[A-Za-z\s']+?\s+(?:is|are)|$)/gi,
      /my\s+([A-Za-z\s']+?)\s+(?:is|:)\s*(.+?)(?=\s*[,;]|\s+my\s+|$)/gi,
    ];
    
    patterns.forEach(pattern => {
      let match;
      const msg = message;
      pattern.lastIndex = 0;
      while ((match = pattern.exec(msg)) !== null) {
        let key = match[1].trim().toLowerCase();
        let value = match[2].trim();
        
        key = key.replace(/^(my|the|a|an)\s+/i, '');
        
        const abbreviations = {
          'dob': 'date of birth',
          'ssn': 'social security number',
          'ph': 'phone',
          'tel': 'phone',
          'addr': 'address',
          'bday': 'birthday',
          'email addr': 'email',
          'e-mail': 'email',
          'cell': 'phone',
          'mobile': 'phone'
        };
        
        if (abbreviations[key]) {
          key = abbreviations[key];
        }
        
        fields[key] = value;
      }
    });
    
    return fields;
  };

  const generateAIResponse = (userMessage, extractedFields) => {
    const fieldCount = Object.keys(extractedFields).length;
    const totalFieldsCount = Object.keys(savedFields).length + fieldCount;
    
    const responses = [];
    
    if (fieldCount === 0) {
      const greetings = ['hi', 'hello', 'hey', 'greetings'];
      if (greetings.some(g => userMessage.toLowerCase().includes(g))) {
        return "Hello! I'm your AI form assistant powered by Claude. I can help you fill out your PDF form quickly. Just tell me your information naturally, like 'My name is John Doe and my email is john@example.com', and I'll extract and place it on your form.";
      }
      
      if (totalFieldsCount === 0) {
        return "I'm ready to help! Please share your information in a natural way. For example:\n• 'My name is Sarah Johnson'\n• 'Email: sarah@email.com, Phone: 555-1234'\n• 'I was born on January 15, 1990'\n\nI'll understand and save everything you provide.";
      } else {
        return "I didn't catch any new information from that message. Could you try rephrasing? For example: 'My address is 123 Main St' or 'Company: Acme Corp'.";
      }
    }
    
    if (fieldCount === 1) {
      const [field, value] = Object.entries(extractedFields)[0];
      responses.push(`Perfect! I've saved your ${field}: "${value}".`);
    } else if (fieldCount === 2) {
      const fields = Object.keys(extractedFields).join(' and ');
      responses.push(`Great! I've captured your ${fields}.`);
    } else {
      const fields = Object.keys(extractedFields);
      const lastField = fields.pop();
      responses.push(`Excellent! I've saved ${fieldCount} fields: ${fields.join(', ')}, and ${lastField}.`);
    }
    
    if (totalFieldsCount >= 3) {
      responses.push(`You've provided ${totalFieldsCount} field${totalFieldsCount > 1 ? 's' : ''} so far. Ready to place them on your form? Click "Apply to Form" below, or keep adding more information.`);
    } else {
      responses.push("Feel free to provide more information, or click 'Apply to Form' when you're ready to place these fields on your PDF.");
    }
    
    return responses.join(' ');
  };

  const handleAIMessage = async () => {
    if (!aiInput.trim()) return;
    
    if (currentPlan === 'free') {
      alert('AI Form Assistant is a Pro feature. Please upgrade to access this functionality.');
      return;
    }
    
    const userMsg = { role: 'user', content: aiInput };
    setAiMessages([...aiMessages, userMsg]);
    setAiInput('');
    setAiLoading(true);
    
    try {
      // Use Claude Haiku for field extraction
      const extracted = await extractFieldsWithClaude(aiInput);
      
      const newFields = { ...savedFields, ...extracted };
      setSavedFields(newFields);
      
      const response = generateAIResponse(aiInput, extracted);
      
      const aiMsg = { role: 'ai', content: response };
      setAiMessages([...aiMessages, userMsg, aiMsg]);
    } catch (error) {
      console.error('Error processing message:', error);
      const errorMsg = { 
        role: 'ai', 
        content: 'Sorry, I encountered an error processing your message. Please try again or rephrase your information.' 
      };
      setAiMessages([...aiMessages, userMsg, errorMsg]);
    } finally {
      setAiLoading(false);
    }
  };

  const applyToForm = () => {
    const fields = Object.entries(savedFields).map(([key, value]) => ({ key, value }));
    setPlacementFields(fields);
    setPlacementIndex(0);
    setPlacementMode(true);
    setShowAIModal(false);
  };

  const downloadAsPNG = async () => {
    if (!pdfDoc) {
      alert('No PDF loaded');
      return;
    }
    
    console.log('Starting download for', totalPages, 'pages');
    
    try {
      for (let i = 1; i <= totalPages; i++) {
        console.log('Downloading page', i);
        await downloadPage(i);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      alert(`Downloaded ${totalPages} page(s) successfully!`);
    } catch (error) {
      console.error('Error downloading:', error);
      alert('Error during download: ' + error.message);
    }
  };

  const downloadPage = (pageNum) => {
    return new Promise(async (resolve, reject) => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        const scale = 2;
        const viewport = page.getViewport({ scale: scale });
        
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = viewport.width;
        tempCanvas.height = viewport.height;
        const ctx = tempCanvas.getContext('2d');
        
        console.log('Rendering page', pageNum);
        
        await page.render({ 
          canvasContext: ctx, 
          viewport: viewport 
        }).promise;
        
        console.log('Page rendered, drawing elements');
        
        const pageElements = elements.filter(el => el.page === pageNum);
        let signaturesToLoad = pageElements.filter(el => el.type === 'signature').length;
        let signaturesLoaded = 0;
        
        console.log('Found', pageElements.length, 'elements,', signaturesToLoad, 'signatures');
        
        const completeDownload = () => {
          console.log('Completing download for page', pageNum);
          try {
            const link = document.createElement('a');
            link.download = `edited-page-${pageNum}.png`;
            link.href = tempCanvas.toDataURL('image/png');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            resolve();
          } catch (err) {
            console.error('Error creating download link:', err);
            reject(err);
          }
        };
        
        const checkComplete = () => {
          if (signaturesToLoad === 0 || signaturesLoaded === signaturesToLoad) {
            completeDownload();
          }
        };
        
        pageElements.forEach(el => {
          if (el.type === 'text') {
            const fontSize = el.fontSize || 20;
            ctx.font = `${fontSize * 1.33}px Arial`;
            ctx.fillStyle = 'black';
            ctx.fillText(el.content, el.x * 1.33, el.y * 1.33);
          } else if (el.type === 'signature') {
            const img = new Image();
            img.onload = () => {
              ctx.drawImage(
                img, 
                el.x * 1.33, 
                el.y * 1.33, 
                el.width * 1.33, 
                el.height * 1.33
              );
              signaturesLoaded++;
              checkComplete();
            };
            img.onerror = (err) => {
              console.error('Error loading signature image:', err);
              signaturesLoaded++;
              checkComplete();
            };
            img.src = el.data;
          }
        });
        
        if (signaturesToLoad === 0) {
          completeDownload();
        }
        
      } catch (error) {
        console.error(`Error rendering page ${pageNum}:`, error);
        reject(error);
      }
    });
  };

  const downloadAsPDF = async () => {
    if (!pdfDoc) {
      alert('No PDF loaded');
      return;
    }

    console.log('Starting PDF download');

    try {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      
      await new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });

      const { jsPDF } = window.jspdf;

      const firstPage = await pdfDoc.getPage(1);
      const viewport = firstPage.getViewport({ scale: 2 });
      
      const pdfWidth = viewport.width * 0.264583;
      const pdfHeight = viewport.height * 0.264583;
      
      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pdfWidth, pdfHeight]
      });

      for (let i = 1; i <= totalPages; i++) {
        console.log('Processing page', i, 'for PDF');
        
        const page = await pdfDoc.getPage(i);
        const scale = 2;
        const viewport = page.getViewport({ scale: scale });
        
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = viewport.width;
        tempCanvas.height = viewport.height;
        const ctx = tempCanvas.getContext('2d');
        
        await page.render({ 
          canvasContext: ctx, 
          viewport: viewport 
        }).promise;
        
        const pageElements = elements.filter(el => el.page === i);
        
        pageElements.forEach(el => {
          if (el.type === 'text') {
            const fontSize = el.fontSize || 20;
            ctx.font = `${fontSize * 1.33}px Arial`;
            ctx.fillStyle = 'black';
            ctx.fillText(el.content, el.x * 1.33, el.y * 1.33);
          }
        });
        
        const signaturePromises = pageElements
          .filter(el => el.type === 'signature')
          .map(el => {
            return new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                ctx.drawImage(
                  img, 
                  el.x * 1.33, 
                  el.y * 1.33, 
                  el.width * 1.33, 
                  el.height * 1.33
                );
                resolve();
              };
              img.onerror = resolve;
              img.src = el.data;
            });
          });
        
        await Promise.all(signaturePromises);
        
        const imgData = tempCanvas.toDataURL('image/jpeg', 1.0);
        
        if (i > 1) {
          pdf.addPage([pdfWidth, pdfHeight], pdfWidth > pdfHeight ? 'landscape' : 'portrait');
        }
        
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }

      pdf.save('edited-document.pdf');
      alert('PDF downloaded successfully!');
      
    } catch (error) {
      console.error('Error creating PDF:', error);
      alert('Error creating PDF: ' + error.message);
    }
  };

  const resetDocument = () => {
    setPdfDoc(null);
    setElements([]);
    setHistory([]);
    setSelectedElement(null);
    setCurrentPage(1);
    setTotalPages(0);
    setSavedFields({});
    setAiMessages([]);
    setPlacementMode(false);
  };

  const startEditing = (plan) => {
    setCurrentPlan(plan);
    setShowLanding(false);
  };

  if (showLanding) {
    return (
      <div style={styles.landingContainer}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle}>
              Professional PDF Editing<br />Made Simple
            </h1>
            <p style={styles.heroSubtitle}>
              Edit, sign, and fill PDF documents with ease. Add text, signatures, and use AI to auto-fill forms in seconds.
            </p>
            <div style={styles.heroButtons}>
              <button 
                onClick={() => startEditing('free')}
                style={styles.heroPrimaryButton}
              >
                Get Started Free
              </button>
              <button 
                onClick={() => {
                  document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' });
                }}
                style={styles.heroSecondaryButton}
              >
                View Pricing
              </button>
            </div>
            <p style={styles.heroNote}>
              No credit card required • Free forever plan available
            </p>
          </div>
        </div>

        {/* Features Section */}
        <div style={styles.featuresSection}>
          <div style={styles.sectionContainer}>
            <h2 style={styles.sectionTitle}>Powerful Features</h2>
            <p style={styles.sectionSubtitle}>
              Everything you need to edit PDF documents professionally
            </p>
            
            <div style={styles.featuresGrid}>
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}>✍️</div>
                <h3 style={styles.featureTitle}>Add Text & Signatures</h3>
                <p style={styles.featureDesc}>
                  Click to add text anywhere or draw/type your signature with our intuitive tools.
                </p>
              </div>
              
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}>🎨</div>
                <h3 style={styles.featureTitle}>Resize & Customize</h3>
                <p style={styles.featureDesc}>
                  Resize text and signatures easily. Font sizes adjust dynamically as you scale.
                </p>
              </div>
              
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}>📥</div>
                <h3 style={styles.featureTitle}>Multiple Export Formats</h3>
                <p style={styles.featureDesc}>
                  Download as high-resolution PNG images or professional PDF documents.
                </p>
              </div>
              
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}>↩️</div>
                <h3 style={styles.featureTitle}>Undo & History</h3>
                <p style={styles.featureDesc}>
                  Made a mistake? Undo any action with full history tracking of all changes.
                </p>
              </div>
              
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}>🔒</div>
                <h3 style={styles.featureTitle}>100% Secure</h3>
                <p style={styles.featureDesc}>
                  All processing happens in your browser. Your documents never leave your device.
                </p>
              </div>
              
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}>⚡</div>
                <h3 style={styles.featureTitle}>Fast & Efficient</h3>
                <p style={styles.featureDesc}>
                  Edit multiple pages seamlessly with instant updates and smooth performance.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div id="pricing" style={styles.pricingSection}>
          <div style={styles.sectionContainer}>
            <h2 style={styles.sectionTitle}>Simple, Transparent Pricing</h2>
            <p style={styles.sectionSubtitle}>
              Choose the plan that's right for you
            </p>
            
            <div style={styles.pricingGrid}>
              {/* Free Plan */}
              <div style={styles.pricingCard}>
                <div style={styles.pricingHeader}>
                  <h3 style={styles.pricingPlanName}>Free</h3>
                  <div style={styles.pricingAmount}>
                    <span style={styles.pricingPrice}>$0</span>
                    <span style={styles.pricingPeriod}>/forever</span>
                  </div>
                  <p style={styles.pricingDescription}>
                    Perfect for occasional PDF editing
                  </p>
                </div>
                
                <div style={styles.pricingFeatures}>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#22c55e" />
                    <span>Add text and signatures</span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#22c55e" />
                    <span>Draw or type signatures</span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#22c55e" />
                    <span>Resize and move elements</span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#22c55e" />
                    <span>PNG export</span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#22c55e" />
                    <span>Undo/redo support</span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#22c55e" />
                    <span>Multi-page documents</span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#22c55e" />
                    <span>PDF export</span>
                  </div>
                  <div style={styles.pricingFeatureDisabled}>
                    <X size={20} color="#9ca3af" />
                    <span>AI Form Assistant</span>
                  </div>
                </div>
                
                <button 
                  onClick={() => startEditing('free')}
                  style={styles.pricingButton}
                >
                  Start Free
                </button>
              </div>

              {/* Pro Plan */}
              <div style={{...styles.pricingCard, ...styles.pricingCardPro}}>
                <div style={styles.pricingBadge}>MOST POPULAR</div>
                <div style={styles.pricingHeader}>
                  <h3 style={styles.pricingPlanName}>Pro</h3>
                  <div style={styles.pricingAmount}>
                    <span style={styles.pricingPrice}>$9.99</span>
                    <span style={styles.pricingPeriod}>/month</span>
                  </div>
                  <p style={styles.pricingDescription}>
                    For professionals who need more power
                  </p>
                </div>
                
                <div style={styles.pricingFeatures}>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#3B82F6" />
                    <span><strong>Everything in Free, plus:</strong></span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Sparkles size={20} color="#3B82F6" />
                    <span><strong>AI Form Assistant</strong></span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#3B82F6" />
                    <span>Natural language form filling</span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#3B82F6" />
                    <span>Smart field detection</span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Check size={20} color="#3B82F6" />
                    <span>Priority support</span>
                  </div>
                  <div style={styles.pricingFeature}>
                    <Zap size={20} color="#3B82F6" />
                    <span>Advanced features</span>
                  </div>
                </div>
                
                <button 
                  onClick={() => startEditing('pro')}
                  style={styles.pricingButtonPro}
                >
                  Start Pro Trial
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div style={styles.ctaSection}>
          <div style={styles.ctaContent}>
            <h2 style={styles.ctaTitle}>Ready to Get Started?</h2>
            <p style={styles.ctaSubtitle}>
              Join thousands of professionals editing PDFs efficiently
            </p>
            <button 
              onClick={() => startEditing('free')}
              style={styles.ctaButton}
            >
              Start Editing Now - It's Free
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>
            © 2025 PDF Genie. All rights reserved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.toolbar}>
        <div style={styles.toolbarLeft}>
          <button 
            onClick={() => {
              if (window.confirm('Return to landing page? Any unsaved changes will be lost.')) {
                setShowLanding(true);
                resetDocument();
              }
            }}
            style={styles.logoButton}
            title="Home"
          >
            <span style={styles.logo}>PDF Genie</span>
          </button>
          
          <div style={styles.separator}></div>
        
          <button 
            onClick={resetDocument}
            style={styles.toolButton}
            title="New Document"
          >
            <RefreshCw size={20} />
          </button>
          
          <div style={styles.separator}></div>
          
          <button 
            onClick={() => setSelectedTool('select')}
            style={{...styles.toolButton, ...(selectedTool === 'select' ? styles.activeTool : {})}}
            title="Select"
          >
            <MousePointer size={20} />
          </button>
          
          <button 
            onClick={() => setSelectedTool('text')}
            style={{...styles.toolButton, ...(selectedTool === 'text' ? styles.activeTool : {})}}
            title="Text"
          >
            <Type size={20} />
          </button>
          
          <button 
            onClick={() => setSelectedTool('signature')}
            style={{...styles.toolButton, ...(selectedTool === 'signature' ? styles.activeTool : {})}}
            title="Signature"
          >
            <InkPenIcon size={20} />
          </button>
          
          <div style={styles.separator}></div>
          
          <button 
            onClick={handleUndo}
            style={{...styles.toolButton, ...(history.length === 0 ? styles.disabledButton : {})}}
            disabled={history.length === 0}
            title="Undo"
          >
            <Undo size={20} />
          </button>
          
          <button 
            onClick={handleDelete}
            style={{...styles.toolButton, ...(!selectedElement ? styles.disabledButton : {})}}
            disabled={!selectedElement}
            title="Delete"
          >
            <Trash2 size={20} />
          </button>
          
          <div style={styles.separator}></div>
          
          <button 
            onClick={() => setShowAIModal(true)}
            style={{...styles.toolButton, ...styles.aiButton}}
            title="AI Form Assistant (Pro)"
          >
            <Sparkles size={20} />
          </button>
          
          <button 
            onClick={() => {
              setShowDownloadModal(true);
            }}
            style={{...styles.toolButton, ...styles.downloadButton, ...(!pdfDoc ? styles.disabledButton : {})}}
            disabled={!pdfDoc}
            title="Download"
          >
            <Download size={20} />
          </button>
        </div>
        
        <div style={styles.planBadge}>
          {currentPlan === 'pro' ? (
            <>
              <Zap size={16} />
              <span>Pro Plan</span>
            </>
          ) : (
            <>
              <span>Free Plan</span>
              <button 
                onClick={() => {
                  document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' });
                  setShowLanding(true);
                }}
                style={styles.upgradeButton}
              >
                Upgrade
              </button>
            </>
          )}
        </div>
      </div>

      {placementMode && (
        <div style={styles.placementBanner}>
          <div>
            Click where you want to place: <strong>{placementFields[placementIndex]?.key}</strong>
          </div>
          <div>
            Field {placementIndex + 1} of {placementFields.length}
          </div>
        </div>
      )}

      <div style={styles.content}>
        {!pdfDoc ? (
          <div 
            style={styles.uploadArea}
            onDrop={(e) => {
              e.preventDefault();
              handleFileUpload(e.dataTransfer.files[0]);
            }}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current.click()}
          >
            <Upload size={48} color="#3B82F6" />
            <p style={styles.uploadText}>
              {pdfLibLoaded ? 'Drop PDF here or click to upload' : 'Loading PDF library...'}
            </p>
            <input 
              ref={fileInputRef}
              type="file" 
              accept="application/pdf"
              onChange={(e) => handleFileUpload(e.target.files[0])}
              style={{ display: 'none' }}
              disabled={!pdfLibLoaded}
            />
          </div>
        ) : (
          <>
            <div style={styles.canvasContainer}>
              <canvas 
                ref={canvasRef}
                onClick={handleCanvasClick}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                style={{
                  ...styles.canvas,
                  cursor: selectedTool === 'text' ? 'crosshair' : 
                          selectedTool === 'signature' ? 'crosshair' :
                          placementMode ? 'crosshair' : 'default'
                }}
              />
              
              {textInput && (
                <input 
                  type="text"
                  autoFocus
                  value={textInput.value}
                  onChange={(e) => setTextInput({...textInput, value: e.target.value})}
                  onKeyDown={handleTextInput}
                  onBlur={() => {
                    if (textInput.value.trim()) {
                      const newElement = {
                        id: Date.now(),
                        type: 'text',
                        content: textInput.value,
                        x: textInput.x,
                        y: textInput.y,
                        fontSize: 20,
                        page: currentPage
                      };
                      saveHistory();
                      setElements([...elements, newElement]);
                    }
                    setTextInput(null);
                  }}
                  style={{
                    ...styles.textInput,
                    left: textInput.x,
                    top: textInput.y - 20
                  }}
                />
              )}
            </div>
            
            <div style={styles.navigation}>
              <button 
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                    renderPage(pdfDoc, currentPage - 1);
                  }
                }}
                disabled={currentPage === 1}
                style={styles.navButton}
              >
                Previous
              </button>
              <span style={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
              <button 
                onClick={() => {
                  if (currentPage < totalPages) {
                    setCurrentPage(currentPage + 1);
                    renderPage(pdfDoc, currentPage + 1);
                  }
                }}
                disabled={currentPage === totalPages}
                style={styles.navButton}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {showDownloadModal && (
        <div style={styles.modalBackdrop} onClick={() => setShowDownloadModal(false)}>
          <div style={styles.downloadModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Download Document</h3>
              <button onClick={() => setShowDownloadModal(false)} style={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            
            <p style={styles.downloadDescription}>
              Choose your preferred download format:
            </p>
            
            <div style={styles.downloadOptions}>
              <button 
                onClick={() => {
                  setShowDownloadModal(false);
                  downloadAsPNG();
                }}
                style={styles.downloadOptionButton}
              >
                <div style={styles.downloadOptionIcon}>🖼️</div>
                <div style={styles.downloadOptionTitle}>PNG Images</div>
                <div style={styles.downloadOptionDesc}>
                  Download each page as a separate PNG image file
                </div>
              </button>
              
              <button 
                onClick={() => {
                  setShowDownloadModal(false);
                  downloadAsPDF();
                }}
                style={styles.downloadOptionButton}
              >
                <div style={styles.downloadOptionIcon}>📄</div>
                <div style={styles.downloadOptionTitle}>
                  PDF Document
                </div>
                <div style={styles.downloadOptionDesc}>
                  Download as a single PDF file with all pages
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showSignatureModal && (
        <div style={styles.modalBackdrop} onClick={() => setShowSignatureModal(false)}>
          <div style={styles.signatureModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Create Signature</h3>
              <button onClick={() => setShowSignatureModal(false)} style={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            
            <div style={styles.signatureTabs}>
              <button 
                onClick={() => setSignatureMode('draw')}
                style={{
                  ...styles.tabButton,
                  ...(signatureMode === 'draw' ? styles.activeTab : {})
                }}
              >
                Draw
              </button>
              <button 
                onClick={() => setSignatureMode('type')}
                style={{
                  ...styles.tabButton,
                  ...(signatureMode === 'type' ? styles.activeTab : {})
                }}
              >
                Type
              </button>
            </div>
            
            {signatureMode === 'draw' ? (
              <canvas 
                ref={signatureCanvasRef}
                width={450}
                height={200}
                onMouseDown={handleSignatureMouseDown}
                onMouseMove={handleSignatureMouseMove}
                onMouseUp={handleSignatureMouseUp}
                style={styles.signatureCanvas}
              />
            ) : (
              <div style={styles.typeSignatureContainer}>
                <input
                  type="text"
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder="Type your signature here..."
                  style={styles.typeSignatureInput}
                  autoFocus
                />
                <div style={styles.signaturePreview}>
                  <div style={styles.signaturePreviewText}>
                    {typedSignature || 'Preview'}
                  </div>
                </div>
              </div>
            )}
            
            <div style={styles.modalActions}>
              {signatureMode === 'draw' && (
                <button onClick={clearSignature} style={styles.secondaryButton}>Clear</button>
              )}
              <button onClick={saveSignature} style={styles.primaryButton}>
                Save Signature
              </button>
            </div>
          </div>
        </div>
      )}

      {showAIModal && (
        <div style={styles.modalBackdrop} onClick={() => setShowAIModal(false)}>
          <div style={styles.aiModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                AI Form Assistant {currentPlan === 'free' && '🔒 Pro'}
              </h3>
              <button onClick={() => setShowAIModal(false)} style={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            
            {currentPlan === 'free' ? (
              <div style={styles.proFeatureLock}>
                <Sparkles size={48} color="#3B82F6" />
                <h3 style={styles.proFeatureTitle}>Upgrade to Pro</h3>
                <p style={styles.proFeatureDesc}>
                  The AI Form Assistant is a Pro feature. Upgrade now to use natural language to auto-fill your forms intelligently.
                </p>
                <button 
                  onClick={() => {
                    setShowAIModal(false);
                    setShowLanding(true);
                    setTimeout(() => {
                      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                  style={styles.upgradeButtonLarge}
                >
                  Upgrade to Pro
                </button>
              </div>
            ) : (
              <>
                {Object.keys(savedFields).length > 0 && (
                  <div style={styles.savedFields}>
                    {Object.entries(savedFields).map(([key, value]) => (
                      <span key={key} style={styles.fieldTag}>
                        {key}: {value}
                      </span>
                    ))}
                  </div>
                )}
                
                <div style={styles.chatContainer}>
                  {aiMessages.length === 0 && (
                    <div style={styles.welcomeMessage}>
                      <Sparkles size={24} color="#a855f7" />
                      <p style={styles.welcomeText}>
                        👋 Hi! I'm your AI form filling assistant. I can understand natural language and extract information intelligently.
                      </p>
                      <p style={styles.welcomeExamples}>
                        Try saying things like:<br/>
                        • "My name is Alex Chen and email is alex@email.com"<br/>
                        • "I was born on March 15, 1985"<br/>
                        • "Company: TechCorp, Phone: 555-0123"
                      </p>
                    </div>
                  )}
                  {aiMessages.map((msg, idx) => (
                    <div key={idx} style={msg.role === 'user' ? styles.userMessage : styles.aiMessage}>
                      {msg.content}
                    </div>
                  ))}
                </div>
                
                <div style={styles.inputContainer}>
                  <input 
                    type="text"
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !aiLoading && handleAIMessage()}
                    placeholder="Type naturally... e.g., 'My name is John and my email is john@email.com'"
                    style={styles.aiInput}
                    disabled={aiLoading}
                  />
                  <button 
                    onClick={handleAIMessage} 
                    style={{...styles.sendButton, ...(aiLoading ? styles.disabledButton : {})}}
                    disabled={aiLoading}
                  >
                    {aiLoading ? '...' : <Send size={20} />}
                  </button>
                </div>
                
                {Object.keys(savedFields).length > 0 && (
                  <button onClick={applyToForm} style={styles.applyButton}>
                    Apply to Form
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  // Landing Page Styles
  landingContainer: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  hero: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '80px 24px 100px',
    textAlign: 'center',
    color: 'white'
  },
  heroContent: {
    maxWidth: '800px',
    margin: '0 auto'
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: '700',
    marginBottom: '24px',
    lineHeight: '1.2'
  },
  heroSubtitle: {
    fontSize: '20px',
    marginBottom: '40px',
    opacity: 0.95,
    lineHeight: '1.6'
  },
  heroButtons: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  heroPrimaryButton: {
    padding: '16px 32px',
    backgroundColor: 'white',
    color: '#667eea',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  heroSecondaryButton: {
    padding: '16px 32px',
    backgroundColor: 'transparent',
    color: 'white',
    border: '2px solid white',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  heroNote: {
    marginTop: '24px',
    fontSize: '14px',
    opacity: 0.9
  },
  featuresSection: {
    padding: '80px 24px',
    backgroundColor: '#f9fafb'
  },
  sectionContainer: {
    maxWidth: '1200px',
    margin: '0 auto'
  },
  sectionTitle: {
    fontSize: '36px',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '16px',
    color: '#111827'
  },
  sectionSubtitle: {
    fontSize: '18px',
    textAlign: 'center',
    color: '#6b7280',
    marginBottom: '48px'
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px'
  },
  featureCard: {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  featureIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  featureTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#111827'
  },
  featureDesc: {
    fontSize: '15px',
    color: '#6b7280',
    lineHeight: '1.6'
  },
  pricingSection: {
    padding: '80px 24px',
    backgroundColor: 'white'
  },
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '32px',
    maxWidth: '900px',
    margin: '0 auto'
  },
  pricingCard: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '16px',
    border: '2px solid #e5e7eb',
    position: 'relative',
    transition: 'transform 0.2s, box-shadow 0.2s'
  },
  pricingCardPro: {
    border: '2px solid #3B82F6',
    boxShadow: '0 10px 40px rgba(59, 130, 246, 0.2)',
    transform: 'scale(1.05)'
  },
  pricingBadge: {
    position: 'absolute',
    top: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#3B82F6',
    color: 'white',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.5px'
  },
  pricingHeader: {
    marginBottom: '32px',
    textAlign: 'center'
  },
  pricingPlanName: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '16px',
    color: '#111827'
  },
  pricingAmount: {
    marginBottom: '12px'
  },
  pricingPrice: {
    fontSize: '48px',
    fontWeight: '700',
    color: '#111827'
  },
  pricingPeriod: {
    fontSize: '18px',
    color: '#6b7280'
  },
  pricingDescription: {
    fontSize: '15px',
    color: '#6b7280'
  },
  pricingFeatures: {
    marginBottom: '32px'
  },
  pricingFeature: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    fontSize: '15px',
    color: '#374151'
  },
  pricingFeatureDisabled: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    fontSize: '15px',
    color: '#9ca3af'
  },
  pricingButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  pricingButtonPro: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)'
  },
  ctaSection: {
    padding: '80px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    textAlign: 'center',
    color: 'white'
  },
  ctaContent: {
    maxWidth: '700px',
    margin: '0 auto'
  },
  ctaTitle: {
    fontSize: '40px',
    fontWeight: '700',
    marginBottom: '16px'
  },
  ctaSubtitle: {
    fontSize: '18px',
    marginBottom: '32px',
    opacity: 0.95
  },
  ctaButton: {
    padding: '16px 40px',
    backgroundColor: 'white',
    color: '#667eea',
    border: 'none',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s'
  },
  footer: {
    padding: '32px 24px',
    backgroundColor: '#111827',
    textAlign: 'center'
  },
  footerText: {
    color: '#9ca3af',
    fontSize: '14px'
  },
  
  // Editor Styles
  container: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
    flexWrap: 'wrap',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  logoButton: {
    padding: '8px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: '700',
    color: '#3B82F6'
  },
  logo: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  toolButton: {
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    color: '#374151'
  },
  activeTool: {
    backgroundColor: '#3B82F6',
    color: 'white',
    borderColor: '#3B82F6'
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  aiButton: {
    backgroundColor: '#a855f7',
    color: 'white',
    border: 'none'
  },
  downloadButton: {
    backgroundColor: '#22c55e',
    color: 'white',
    borderColor: '#22c55e'
  },
  separator: {
    width: '1px',
    height: '24px',
    backgroundColor: '#e5e7eb'
  },
  planBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151'
  },
  upgradeButton: {
    padding: '4px 12px',
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    marginLeft: '4px'
  },
  placementBanner: {
    padding: '12px 16px',
    backgroundColor: '#a855f7',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: '500',
    position: 'sticky',
    top: 0,
    zIndex: 999
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px',
    gap: '16px'
  },
  uploadArea: {
    width: '600px',
    height: '400px',
    border: '3px dashed #3B82F6',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: 'white',
    transition: 'all 0.2s'
  },
  uploadText: {
    marginTop: '16px',
    color: '#6b7280',
    fontSize: '16px',
    fontWeight: '500'
  },
  canvasContainer: {
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    padding: '16px'
  },
  canvas: {
    border: '1px solid #e5e7eb',
    borderRadius: '4px'
  },
  textInput: {
    position: 'absolute',
    fontSize: '20px',
    border: '2px solid #3B82F6',
    borderRadius: '4px',
    padding: '4px 8px',
    outline: 'none',
    minWidth: '200px',
    zIndex: 1000
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  navButton: {
    padding: '10px 20px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  pageInfo: {
    fontSize: '14px',
    color: '#374151',
    fontWeight: '500'
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  signatureModal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '500px',
    width: '500px'
  },
  signatureTabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    borderBottom: '2px solid #e5e7eb',
    paddingBottom: '0'
  },
  tabButton: {
    padding: '10px 24px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.2s'
  },
  activeTab: {
    color: '#3B82F6',
    borderBottomColor: '#3B82F6'
  },
  typeSignatureContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '16px'
  },
  typeSignatureInput: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s'
  },
  signaturePreview: {
    height: '200px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9fafb'
  },
  signaturePreviewText: {
    fontFamily: '"Brush Script MT", cursive, serif',
    fontSize: '48px',
    fontStyle: 'italic',
    color: '#000000',
    userSelect: 'none'
  },
  aiModal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '700px',
    maxWidth: '90vw'
  },
  downloadModal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '500px',
    maxWidth: '90vw'
  },
  downloadDescription: {
    color: '#6b7280',
    fontSize: '14px',
    marginBottom: '20px',
    textAlign: 'center'
  },
  downloadOptions: {
    display: 'flex',
    gap: '16px',
    flexDirection: 'column'
  },
  downloadOptionButton: {
    padding: '20px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    backgroundColor: 'white',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px'
  },
  downloadOptionDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  downloadOptionIcon: {
    fontSize: '48px',
    marginBottom: '8px'
  },
  downloadOptionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '4px'
  },
  downloadOptionDesc: {
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.5'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
    color: '#111827'
  },
  closeButton: {
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    color: '#6b7280'
  },
  signatureCanvas: {
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'crosshair',
    marginBottom: '16px',
    display: 'block'
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  },
  primaryButton: {
    padding: '10px 20px',
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  secondaryButton: {
    padding: '10px 20px',
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  savedFields: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px'
  },
  fieldTag: {
    padding: '4px 12px',
    backgroundColor: '#a855f7',
    color: 'white',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '500'
  },
  chatContainer: {
    height: '256px',
    overflowY: 'auto',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  welcomeMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    textAlign: 'center',
    gap: '12px'
  },
  welcomeText: {
    fontSize: '15px',
    color: '#374151',
    margin: '0',
    fontWeight: '500'
  },
  welcomeExamples: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '0',
    lineHeight: '1.8',
    textAlign: 'left',
    backgroundColor: 'white',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  userMessage: {
    alignSelf: 'flex-start',
    maxWidth: '70%',
    padding: '10px 14px',
    backgroundColor: '#3B82F6',
    color: 'white',
    borderRadius: '12px',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  aiMessage: {
    alignSelf: 'flex-end',
    maxWidth: '70%',
    padding: '10px 14px',
    backgroundColor: '#e5e7eb',
    color: '#111827',
    borderRadius: '12px',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  inputContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px'
  },
  aiInput: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none'
  },
  sendButton: {
    padding: '10px 14px',
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  applyButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#a855f7',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600'
  },
  proFeatureLock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    textAlign: 'center'
  },
  proFeatureTitle: {
    fontSize: '24px',
    fontWeight: '700',
    marginTop: '16px',
    marginBottom: '12px',
    color: '#111827'
  },
  proFeatureDesc: {
    fontSize: '15px',
    color: '#6b7280',
    marginBottom: '24px',
    lineHeight: '1.6'
  },
  upgradeButtonLarge: {
    padding: '14px 32px',
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)'
  }
};

export default PDFGenie;